import { parseArticleBody } from '@/lib/cms-article-body';
import type { CmsArticle } from '@/lib/cms-types';
import { slugifyGuideTitle } from '@/lib/guide-routes';
import type { ArticleContent } from '@/lib/articleContent';
import { isRecentlyUpdated } from '@/utils/recentlyUpdated';
import { estimateReadingTimeMinutes } from '@/utils/readingTime';

export type PublicGuideListItem = {
  key: string;
  slug: string;
  title: string;
  updatedAt: string;
  publishedAt?: string | null;
  popular: boolean;
  featured: boolean;
  views: number;
  readingTime: number;
  recentlyUpdated: boolean;
  /** Ícone CMS do guia (fallback: ícone da categoria na UI). */
  icon?: string | null;
  cmsId?: string;
};

function formatPublicUpdatedAt(iso: string): string {
  try {
    return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'medium' }).format(new Date(iso));
  } catch {
    return iso;
  }
}

export function getCmsArticleSlug(article: CmsArticle): string {
  if (article.slug?.trim()) return article.slug.trim().toLowerCase();
  return slugifyGuideTitle(article.title);
}

export function isPublishedGuide(article: CmsArticle): boolean {
  return article.status === 'published';
}

export function resolveGuideSlug(
  title: string,
  categoryId: string,
  articles: CmsArticle[],
  excludeArticleId?: string
): string {
  const base = slugifyGuideTitle(title) || 'guia';
  const used = new Set<string>();

  for (const article of articles) {
    if (article.categoryId !== categoryId) continue;
    if (excludeArticleId && article.id === excludeArticleId) continue;
    used.add(getCmsArticleSlug(article));
  }

  if (!used.has(base)) return base;
  let n = 2;
  while (used.has(`${base}-${n}`)) n += 1;
  return `${base}-${n}`;
}

function readingTimeForArticle(cms: CmsArticle): number {
  if (cms.readingTime && cms.readingTime > 0) return cms.readingTime;
  const body = parseArticleBody(cms.content);
  const text = [body?.intro ?? '', ...(body?.blocks ?? []).map((b) => JSON.stringify(b))].join(' ');
  return estimateReadingTimeMinutes(text);
}

export function buildPublicGuideList(
  categoryId: string,
  cmsArticles: CmsArticle[]
): PublicGuideListItem[] {
  return cmsArticles
    .filter((cms) => cms.categoryId === categoryId && isPublishedGuide(cms))
    .map((cms) => {
      const featured = Boolean(cms.featured);
      return {
        key: `cms-${cms.id}`,
        slug: getCmsArticleSlug(cms),
        title: cms.title,
        updatedAt: formatPublicUpdatedAt(cms.updatedAt),
        publishedAt: cms.publishedAt ?? cms.createdAt,
        popular: featured,
        featured,
        views: cms.views ?? 0,
        readingTime: readingTimeForArticle(cms),
        recentlyUpdated:
          cms.recentlyUpdated ??
          isRecentlyUpdated(cms.updatedAt, cms.publishedAt ?? cms.createdAt),
        icon: cms.icon?.trim() || null,
        cmsId: cms.id,
      };
    });
}

export function findPublicGuideListItem(
  categoryId: string,
  slugOrId: string,
  cmsArticles: CmsArticle[]
): PublicGuideListItem | null {
  const key = slugOrId.trim().toLowerCase();
  return buildPublicGuideList(categoryId, cmsArticles).find((a) => a.slug === key) ?? null;
}

export function resolvePublicGuideContent(
  categoryId: string,
  item: PublicGuideListItem,
  cmsArticles: CmsArticle[]
): { title: string; body: ArticleContent | undefined } {
  const cmsPublished = item.cmsId
    ? cmsArticles.find((a) => a.id === item.cmsId && isPublishedGuide(a))
    : cmsArticles.find(
        (a) =>
          a.categoryId === categoryId &&
          isPublishedGuide(a) &&
          getCmsArticleSlug(a) === item.slug
      );

  if (cmsPublished) {
    return {
      title: cmsPublished.title,
      body: parseArticleBody(cmsPublished.content),
    };
  }

  return { title: item.title, body: undefined };
}
